import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Modal,
  Box,
  Typography,
  Slide,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  IconButton,
  Popover,
} from "@mui/material";
import { PaletteOutlined } from "@mui/icons-material";
import { ChromePicker } from "react-color";
import { membershipApis } from "../../Data/axios";
import { getUserInfo } from "../../Data/UserData";

export default function AddMembership({ open, handleClose, onMembershipAdded }) {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [discount, setDiscount] = useState("");
  const [calls, setCalls] = useState("");
  const [jobsPost, setJobsPost] = useState("");
  const [validity, setValidity] = useState("");
  const [userType, setUserType] = useState("");
  const [visibility, setVisibility] = useState("");
  const [color, setColor] = useState("#1d82f5");
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);

  const membershipTypes = ["Classified", "Bucket", "Premium"];

  const handleFormReset = () => {
    setName("");
    setPrice("");
    setDiscount("");
    setCalls("");
    setJobsPost("");
    setValidity("");
    setUserType("");
    setVisibility("");
    setColor("#1d82f5");
  };

  const handleCloseModal = () => {
    handleFormReset();
    handleClose();
  };

  const handleColorPickerOpen = (event) => {
    setColorPickerAnchor(event.currentTarget);
  };

  const handleColorPickerClose = () => {
    setColorPickerAnchor(null);
  };

  const handleColorChange = (newColor) => {
    setColor(newColor.hex);
  };

const handleSaveMembership = async () => {
  try {
    if (!name || !price || !userType || !visibility) {
      alert("Please fill in all required fields");
      return;
    }

    // Validate data formats
    if (isNaN(parseFloat(price))) {
      alert("Price must be a valid number");
      return;
    }

    if (discount && isNaN(parseFloat(discount))) {
      alert("Discount must be a valid number");
      return;
    }

    if (calls && isNaN(parseInt(calls))) {
      alert("Calls must be a valid number");
      return;
    }

    if (jobsPost && isNaN(parseInt(jobsPost))) {
      alert("Job posts must be a valid number");
      return;
    }

    if (validity && isNaN(parseInt(validity))) {
      alert("Validity must be a valid number");
      return;
    }

    // Create and populate formData with default values for empty fields
    const formData = new FormData();
    formData.append("membership", name);
    formData.append("price", price);
    formData.append("discount", discount || "0"); 
    formData.append("calls", calls || "0");
    formData.append("jobspost", jobsPost || "0");
    formData.append("validity", validity || "30");
    formData.append("role", userType);
    formData.append("color", color.replace('#', '')); // Remove # from color hex if present
    formData.append("access", visibility);
    formData.append("created_by", getUserInfo());

    console.log("Submitting membership data:");
    for (let [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`);
    }

    // Check connection before submitting
    if (!navigator.onLine) {
      alert("You appear to be offline. Please check your internet connection and try again.");
      return;
    }

    try {
      const response = await membershipApis.add(formData);
      
      // Check if response has a success property
      if (response && response.success) {
        alert("Membership added successfully!");
        if (onMembershipAdded) onMembershipAdded();
        handleCloseModal();
        return;
      }
      
      // If we have error code 1602 but the data was actually added
      if (response && response.error?.code === 1602) {
        console.log("API returned error 1602, but the membership may have been added successfully");
        // Call the onMembershipAdded callback to refresh the data
        if (onMembershipAdded) onMembershipAdded();
        handleCloseModal();
        return;
      }
      
      // For other response issues
      console.error("API Error:", response);
      alert(response?.message || "Failed to add membership. Please try again.");
      
    } catch (innerError) {
      console.error("Inner catch error:", innerError);
      
      // Check if the membership was actually added despite the error
      if (onMembershipAdded) {
        // Give the database a moment to complete the transaction
        setTimeout(() => {
          onMembershipAdded();
          handleCloseModal();
        }, 500);
      }
    }
    
  } catch (error) {
    console.error("Error adding membership:", error);
    
    // Even if there's an error, the membership might have been added
    if (onMembershipAdded) {
      // Refresh data to check if it was actually added
      onMembershipAdded();
      handleCloseModal();
    } else {
      let errorMessage = "An error occurred while adding the membership.";
      
      if (error.response) {
        console.error("Error response data:", error.response.data);
        errorMessage = error.response.data?.message || 
          `Server error (${error.response.status}): ${error.response.statusText}`;
      } else if (error.request) {
        errorMessage = "No response received from server. Please check your connection.";
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = "Request timed out. Please try again.";
      }

      alert(errorMessage);
    }
  }
};

  const inputProps = {
    sx: {
      bgcolor: "#f9f9f9",
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: "#e0e0e0",
        },
        "&:hover fieldset": {
          borderColor: "#bdbdbd",
        },
      },
      "& .MuiInputBase-input": {
        color: "#424242",
      },
    },
  };

  const openColorPicker = Boolean(colorPickerAnchor);
  const colorPickerId = openColorPicker ? "color-picker-popover" : undefined;

  return (
    <Modal open={open} onClose={handleCloseModal} closeAfterTransition>
      <Slide direction="left" in={open}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "85%",
            height: "100%",
            bgcolor: "#f5f5f5",
            boxShadow: 24,
            p: 4,
            overflow: "auto",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h3" component="h2" sx={{ color: "#212121" }}>
              Add New Membership
            </Typography>
            <Button
              onClick={handleCloseModal}
              variant="outlined"
              sx={{
                borderColor: "#bdbdbd",
                color: "#424242",
                "&:hover": {
                  borderColor: "#9e9e9e",
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Close
            </Button>
          </Box>
          <Card
            sx={{
              p: 2,
              bgcolor: "#ffffff",
              boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
            }}
          >
            <CardContent>
              <Box sx={{ mb: 4 }}>
                <Grid container spacing={3}>
                  {/* Membership Information */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ color: "#212121", mb: 2 }}>
                      Membership Information
                    </Typography>
                  </Grid>

                  {/* Membership Type */}
                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{ color: "#212121" }}
                    >
                      Membership Type <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        displayEmpty
                        sx={{
                          backgroundColor: "#f9f9f9",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e0e0e0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#bdbdbd",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1d82f5",
                          },
                          fontFamily: "Ubuntu, sans-serif",
                        }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return (
                              <em
                                style={{
                                  color: "#757575",
                                  fontFamily: "Ubuntu, sans-serif",
                                }}
                              >
                                Select membership type
                              </em>
                            );
                          }
                          return selected;
                        }}
                        inputProps={{ "aria-label": "Select membership type" }}
                      >
                        <MenuItem
                          disabled
                          value=""
                          sx={{ fontFamily: "Ubuntu, sans-serif" }}
                        >
                          <em>Select membership type</em>
                        </MenuItem>
                        {membershipTypes.map((type) => (
                          <MenuItem
                            key={type}
                            value={type}
                            sx={{ fontFamily: "Ubuntu, sans-serif" }}
                          >
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Price */}
                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{ color: "#212121" }}
                    >
                      Price <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter price"
                      size="small"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      InputProps={{
                        ...inputProps,
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Discount */}
                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{ color: "#212121" }}
                    >
                      Discount
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter discount amount"
                      size="small"
                      value={discount}
                      onChange={(e) => setDiscount(e.target.value)}
                      InputProps={{
                        ...inputProps,
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Calls */}
                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{ color: "#212121" }}
                    >
                      Calls
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="e.g. 50"
                      size="small"
                      value={calls}
                      onChange={(e) => setCalls(e.target.value)}
                      InputProps={inputProps}
                    />
                  </Grid>

                  {/* Job Posts */}
                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{ color: "#212121" }}
                    >
                      Job Posts
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="e.g. 25"
                      size="small"
                      value={jobsPost}
                      onChange={(e) => setJobsPost(e.target.value)}
                      InputProps={inputProps}
                    />
                  </Grid>

                  {/* Validity */}
                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{ color: "#212121" }}
                    >
                      Validity (Days)
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="e.g. 30"
                      size="small"
                      value={validity}
                      onChange={(e) => setValidity(e.target.value)}
                      InputProps={inputProps}
                    />
                  </Grid>

                  {/* User Type */}
                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{ color: "#212121" }}
                    >
                      User Type <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={userType}
                        onChange={(e) => setUserType(e.target.value)}
                        displayEmpty
                        sx={{
                          backgroundColor: "#f9f9f9",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e0e0e0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#bdbdbd",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1d82f5",
                          },
                          fontFamily: "Ubuntu, sans-serif",
                        }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return (
                              <em
                                style={{
                                  color: "#757575",
                                  fontFamily: "Ubuntu, sans-serif",
                                }}
                              >
                                Select user type
                              </em>
                            );
                          }
                          return selected === "1"
                            ? "Driver"
                            : selected === "2"
                            ? "Owner"
                            : selected === "3"
                            ? "Dhaba"
                            : selected === "4"
                            ? "Mechanic"
                            : selected === "5"
                            ? "Crane Owner"
                            : selected === "6"
                            ? "Crane Operator"
                            : selected;
                        }}
                        inputProps={{ "aria-label": "Select user type" }}
                      >
                        <MenuItem
                          disabled
                          value=""
                          sx={{ fontFamily: "Ubuntu, sans-serif" }}
                        >
                          <em>Select user type</em>
                        </MenuItem>
                        <MenuItem
                          value="1"
                          sx={{ fontFamily: "Ubuntu, sans-serif" }}
                        >
                          Driver
                        </MenuItem>
                        <MenuItem
                          value="2"
                          sx={{ fontFamily: "Ubuntu, sans-serif" }}
                        >
                          Owner
                        </MenuItem>
                        <MenuItem
                          value="3"
                          sx={{ fontFamily: "Ubuntu, sans-serif" }}
                        >
                          Dhaba
                        </MenuItem>
                        <MenuItem
                          value="4"
                          sx={{ fontFamily: "Ubuntu, sans-serif" }}
                        >
                          Mechanic
                        </MenuItem>
                        <MenuItem
                          value="5"
                          sx={{ fontFamily: "Ubuntu, sans-serif" }}
                        >
                          Crane Owner
                        </MenuItem>
                        <MenuItem
                          value="6"
                          sx={{ fontFamily: "Ubuntu, sans-serif" }}
                        >
                          Crane Operator
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Access Visibility */}
                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{ color: "#212121" }}
                    >
                      Access Visibility <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={visibility}
                        onChange={(e) => setVisibility(e.target.value)}
                        displayEmpty
                        sx={{
                          backgroundColor: "#f9f9f9",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e0e0e0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#bdbdbd",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1d82f5",
                          },
                          fontFamily: "Ubuntu, sans-serif",
                        }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return (
                              <em
                                style={{
                                  color: "#757575",
                                  fontFamily: "Ubuntu, sans-serif",
                                }}
                              >
                                Select access visibility
                              </em>
                            );
                          }
                          return selected === "1"
                            ? "Location"
                            : selected === "2"
                            ? "Nation Wide"
                            : selected;
                        }}
                        inputProps={{ "aria-label": "Select access visibility" }}
                      >
                        <MenuItem
                          disabled
                          value=""
                          sx={{ fontFamily: "Ubuntu, sans-serif" }}
                        >
                          <em>Select access visibility</em>
                        </MenuItem>
                        <MenuItem
                          value="1"
                          sx={{ fontFamily: "Ubuntu, sans-serif" }}
                        >
                          Location
                        </MenuItem>
                        <MenuItem
                          value="2"
                          sx={{ fontFamily: "Ubuntu, sans-serif" }}
                        >
                          Nation Wide
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Color Picker */}
                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="body1"
                      mb={1}
                      sx={{ color: "#212121" }}
                    >
                      Color
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={color}
                      onClick={handleColorPickerOpen}
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                bgcolor: color,
                                borderRadius: 1,
                                border: "1px solid #e0e0e0",
                              }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={handleColorPickerOpen}>
                              <PaletteOutlined />
                            </IconButton>
                          </InputAdornment>
                        ),
                        sx: {
                          ...inputProps.sx,
                          cursor: "pointer",
                        },
                      }}
                    />
                    <Popover
                      id={colorPickerId}
                      open={openColorPicker}
                      anchorEl={colorPickerAnchor}
                      onClose={handleColorPickerClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                    >
                      <ChromePicker
                        color={color}
                        onChangeComplete={handleColorChange}
                        disableAlpha
                      />
                    </Popover>
                  </Grid>
                </Grid>
              </Box>

              {/* Form buttons */}
              <Box display="flex" gap={2} mt={3}>
                <Button
                  variant="contained"
                  onClick={handleSaveMembership}
                  sx={{
                    bgcolor: "#4285F4",
                    color: "white",
                    "&:hover": { bgcolor: "#3367D6" },
                    fontWeight: 500,
                    textTransform: "none",
                    padding: "8px 16px",
                  }}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCloseModal}
                  sx={{
                    borderColor: "#bdbdbd",
                    color: "#424242",
                    "&:hover": {
                      borderColor: "#9e9e9e",
                      bgcolor: "rgba(0, 0, 0, 0.04)",
                    },
                    textTransform: "none",
                    padding: "8px 16px",
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Slide>
    </Modal>
  );
}